import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../services/api";
import Constants from "../../services/constants";
import {
  hideLoader,
  showLoader,
  showSkelton,
  hideSkelton,
} from "./loaderSlice";
import { showToast } from "./toastSlice";

const initialState = {
  isAvailable: false,
  functionsList: [],
  functionDetails: {
    logs: [],
    metrics: {},
    details: {},
    variables: {},
    testing: null,
    settings: {},
  },
};

export const createPandaEdgeFunction = createAsyncThunk(
  "pandaEdge/create",
  async (data, { dispatch }) => {
    try {
      dispatch(showLoader());
      const response = await api("post", Constants.END_POINT.functions, data);
      if (response?.success === true) {
        dispatch(
          showToast({ severity: "success", summary: response?.message })
        );
        // Reset isAvailable to false after successful creation
        dispatch(resetAvailability());
        return response?.data;
      } else {
        dispatch(
          showToast({
            severity: "error",
            summary: response?.message?.message || response?.message,
          })
        );
        return Promise.reject(response?.message);
      }
    } catch (error) {
      dispatch(
        showToast({
          severity: "error",
          summary: error?.message?.message || error?.message,
        })
      );
      return Promise.reject("An error occurred.");
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const editPandaEdgeFunction = createAsyncThunk(
  "pandaEdge/edit",
  async (payload, { dispatch }) => {
    try {
      dispatch(showLoader());
      const response = await api(
        "put",
        `${Constants.END_POINT.functions}/${payload?.id}`,
        payload
      );

      if (response?.success === true) {
        dispatch(
          showToast({ severity: "success", summary: response?.message })
        );
        // Reset isAvailable to false after successful creation
        dispatch(resetAvailability());
        return response?.data;
      } else {
        dispatch(
          showToast({
            severity: "error",
            summary: response?.message?.message || response?.message,
          })
        );
        return Promise.reject(response?.message);
      }
    } catch (error) {
      dispatch(
        showToast({
          severity: "error",
          summary: error?.message?.message || error?.message,
        })
      );
      return Promise.reject("An error occurred.");
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const deletePandaEdgeFunction = createAsyncThunk(
  "pandaEdge/delete",
  async ({ pandaEdgeId, navigate }, { dispatch }) => {
    try {
      dispatch(showLoader());
      const response = await api(
        "delete",
        `${Constants.END_POINT.functions}/${pandaEdgeId}`,
        {}
      );
      if (response?.success) {
        dispatch(
          showToast({ severity: "success", summary: response?.message })
        );
        navigate("/pandaEdge");
      } else {
        dispatch(
          showToast({
            severity: "error",
            summary: response?.message?.message || response?.message,
          })
        );
        return Promise.reject(response?.message);
      }
    } catch (error) {
      dispatch(
        showToast({
          severity: "error",
          summary: error?.message?.message || error?.message,
        })
      );
      return Promise.reject("An error occurred.");
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const checkFunctionNameAvailabilityAction = createAsyncThunk(
  "functions/check-name-availability",
  async ({ data }, { dispatch }) => {
    let { name } = data;
    try {
      dispatch(setAvailability(null));
      const response = await api(
        "get",
        Constants.END_POINT.functions + `/check-name-availability?name=${name}`,
        {}
      );
      if (response.data?.availability === true) {
        dispatch(setAvailability(response?.data?.availability));
        dispatch(
          showToast({
            severity: "success",
            summary: "Function name is available!",
          })
        );
      } else {
        dispatch(setAvailability(response?.data?.availability));
        dispatch(
          showToast({
            severity: "error",
            summary: "Function name is not available!",
          })
        );
        return Promise.reject(response?.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error?.message }));
      return Promise.reject("An error occurred.");
    } finally {
      //   dispatch(hideLoader());
    }
  }
);

export const fetchFunctionsList = createAsyncThunk(
  "pandaEdge/fetchFunctions",
  async ({ data }, { dispatch }) => {
    let { page, pageSize, searchTerm } = data;
    try {
      dispatch(showLoader());
      const response = await api(
        "get",
        Constants.END_POINT.functions +
          `?page=${page}&pageSize=${pageSize}&search=${searchTerm}`
      );
      const functionsData = response?.data?.result;
      const totalRecordsCount = response?.data?.count;

      dispatch(setFunctionsList({ functionsData, totalRecordsCount }));
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred while fetching functions.");
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const fetchTabDataAction = createAsyncThunk(
  "pandaEdge/fetchTabData",
  async ({ selectedRowId, type, timeWindow }, { dispatch }) => {
    try {
      dispatch(showLoader());

      const response = await api(
        "get",
        Constants.END_POINT.functions +
          `/${selectedRowId}?type=${type}&timeWindow=${timeWindow}`,
        {}
      );

      if (!response?.success) {
        dispatch(
          showToast({
            severity: "error",
            summary: response?.message || "Failed to fetch data.",
          })
        );
        return Promise.reject(response?.message || "Failed to fetch data.");
      }

      if (type === "logs") {
        if (response?.data) {
          const logMessages = response?.data.flatMap((logStream) =>
            logStream.events.map((event) => {
              const formattedTime = new Date(event.timestamp).toLocaleString(); // Convert timestamp to readable format
              return `${formattedTime}   ===>   ${event.message}`;
            })
          );
          dispatch(setFunctionDetails({ type, data: logMessages })); // Set logs in Redux
        } else {
          // Show the message if no logs are available
          dispatch(setFunctionDetails({ type, data: ["No logs available."] }));
        }
      } else if (type === "metrics") {
        const metricsData = response?.data;
        const invocationsData = formatChartData(
          metricsData?.invocations,
          "Sum",
          "Invocations/Second"
        );
        const executionTimeData = formatChartData(
          metricsData?.executionTime,
          "Average",
          "Execution Time"
        );
        const errorRateData = formatChartData(
          metricsData?.errorRate,
          "Sum",
          "Error Rate"
        );

        dispatch(
          setFunctionDetails({
            type,
            data: {
              invocations: invocationsData,
              executionTime: executionTimeData,
              errorRate: errorRateData,
            },
          })
        );
      } else {
        // Handle other tabs like details and variables
        dispatch(setFunctionDetails({ type, data: response.data }));
      }
    } catch (error) {
      dispatch(
        showToast({
          severity: "error",
          summary: error?.message?.message || error?.message,
        })
      );
      return Promise.reject(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

// Helper function to format metric data
const formatChartData = (metricData, valueKey, name) => {
  return {
    categories: metricData?.map((item) =>
      new Date(item.Timestamp).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      })
    ),
    data: [
      {
        name: name,
        data: metricData?.map((item) => item[valueKey]),
      },
    ],
  };
};

const pandaEdgeSlice = createSlice({
  name: "pandaEdge",
  initialState,
  reducers: {
    setFunctionsList: (state, action) => {
      state.functionsList = action.payload.functionsData;
      state.totalRecords = action.payload.totalRecordsCount;
    },
    setFunctionDetails: (state, action) => {
      const { type, data } = action.payload;
      if (type === "logs") {
        state.functionDetails.logs = data;
      } else if (type === "metrics") {
        state.functionDetails.metrics = data;
      } else if (type === "details") {
        state.functionDetails.details = data;
      } else if (type === "variables") {
        state.functionDetails.variables = data;
      } else if (type === "testing") {
        state.functionDetails.testing = data;
      } else if (type === "settings") {
        state.functionDetails.settings = data;
      }
    },
    setAvailability: (state, action) => {
      state.isAvailable = action.payload;
    },
    resetAvailability: (state) => {
      state.isAvailable = false; // Reset isAvailable to false
    },
  },
});

export const {
  setAvailability,
  setFunctionsList,
  setFunctionDetails,
  resetAvailability,
  // setFunctionNameAvailability,
} = pandaEdgeSlice.actions;

export default pandaEdgeSlice.reducer;
